import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  formatLong,
  formatShortDate,
  formatEuro,
  statusLabel,
  cancelledByLabel,
  paymentLabel,
  sourceLabel,
  translateServiceLabel,
  formatBookingCustomerNotes,
  internalNotesValue,
} from "./formatters";

const brandLabel = (t, brand) => {
  if (brand === "tawan-spa") return t("site.tawanSpa");
  if (brand === "dr-hoffmann" || brand === "dr-hoffmann-massage") {
    return t("site.drHoffmann");
  }
  return brand || "";
};

const studioScheduleLabel = (t, value) => {
  if (value === "thursday") return t("calendar.bookingDetail.studioThursday");
  if (value === "weekday") return t("calendar.bookingDetail.studioWeekday");
  return value;
};

const formatTime = (date, dateLocale) =>
  new Date(date).toLocaleTimeString(dateLocale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  });

const formatServiceAddress = (addr) => {
  if (!addr) return "";
  const lines = [
    addr.companyName,
    [addr.street, [addr.postalCode, addr.city].filter(Boolean).join(" ")].filter(Boolean).join(", "),
  ].filter(Boolean);
  return lines.join("\n");
};

const serviceOptions = [
  {
    value: "wakeup",
    serviceKey: "dr-hoffmann-wakeup-studio",
    serviceLabel: "Wake-Up Massage",
    durationMinutes: 5,
    priceCents: 1000,
    location: "Cremon 11, 20457 Hamburg",
    label: "Wake-Up Massage",
  },
  {
    value: "classic",
    serviceKey: "dr-hoffmann-classic-studio",
    serviceLabel: "Classic Massage",
    durationMinutes: 10,
    priceCents: 3900,
    location: "Cremon 11, 20457 Hamburg",
    label: "Classic Massage",
  },
  {
    value: "intense",
    serviceKey: "dr-hoffmann-premium-studio",
    serviceLabel: "Intense Massage",
    durationMinutes: 60,
    priceCents: 12500,
    location: "Cremon 11, 20457 Hamburg",
    label: "Intense Massage",
  },
];

const toBerlinDateTimeInput = (date) => {
  if (!date) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(date));
  const part = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
};

const centsToEuroInput = (cents) =>
  Number.isFinite(Number(cents)) ? (Number(cents) / 100).toFixed(2) : "";

const euroInputToCents = (value) => {
  const normalized = String(value || "").replace(",", ".").trim();
  if (!normalized) return 0;
  const amount = Number(normalized);
  if (!Number.isFinite(amount) || amount < 0) return null;
  return Math.round(amount * 100);
};

const serviceValueForBooking = (booking) => {
  const key = String(booking?.serviceKey || booking?.serviceLabel || "").toLowerCase();
  if (key.includes("wakeup") || key.includes("wake-up")) return "wakeup";
  if (key.includes("classic")) return "classic";
  if (key.includes("premium") || key.includes("intense")) return "intense";
  return "";
};

const buildInitialForm = (booking) => {
  const serviceValue = serviceValueForBooking(booking);
  const option = serviceOptions.find((item) => item.value === serviceValue);
  return {
    serviceValue,
    serviceKey: option?.serviceKey || booking?.serviceKey || "",
    serviceLabel: option?.serviceLabel || booking?.serviceLabel || "",
    durationMinutes: String(booking?.durationMinutes || option?.durationMinutes || ""),
    startLocal: toBerlinDateTimeInput(booking?.start),
    price: centsToEuroInput(booking?.priceCents),
    location: booking?.location || "",
    customerNotes: formatBookingCustomerNotes(booking),
  };
};

const DetailRow = ({ label, value, multiline = false }) => (
  <>
    <dt>{label}</dt>
    <dd className={multiline ? "team-booking-detail-multiline" : undefined}>{value}</dd>
  </>
);

const TeamBookingDetailDialog = ({
  open,
  booking,
  dateLocale,
  locale,
  t,
  showSource = false,
  showBrand = false,
  statusOptions = [],
  statusSaving = false,
  onStatusChange,
  onBookingSave,
  onClose,
}) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => buildInitialForm(booking));
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    setEditing(false);
    setSaveError(null);
    setSaving(false);
    setForm(buildInitialForm(booking));
  }, [booking, open]);

  if (!open || !booking) return null;

  const empty = t("calendar.bookingDetail.none");
  const customerName = [booking.customer?.firstName, booking.customer?.lastName]
    .filter(Boolean)
    .join(" ");
  const canEditStatus =
    typeof onStatusChange === "function" &&
    statusOptions.length > 0 &&
    booking.status !== "cancelled" &&
    booking.status !== "pending-email-verification" &&
    booking.status !== "reserved-awaiting-payment" &&
    booking.status !== "awaiting-immediate-payment" &&
    booking.status !== "expired" &&
    booking.status !== "pending-payment";
  const canEditBooking =
    typeof onBookingSave === "function" &&
    booking.status !== "cancelled" &&
    booking.status !== "pending-email-verification" &&
    booking.status !== "reserved-awaiting-payment" &&
    booking.status !== "awaiting-immediate-payment" &&
    booking.status !== "expired" &&
    booking.status !== "pending-payment";
  const currentServiceOptions = serviceOptions;

  const selectedService = currentServiceOptions.find(
    (option) => option.value === form.serviceValue
  );

  const updateForm = (field) => (e) => {
    const value = e.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleServiceChange = (e) => {
    const option = currentServiceOptions.find((item) => item.value === e.target.value);
    if (!option) return;
    setForm((current) => ({
      ...current,
      serviceValue: option.value,
      serviceKey: option.serviceKey,
      serviceLabel: option.serviceLabel,
      durationMinutes: String(option.durationMinutes),
      price: centsToEuroInput(option.priceCents),
      location: option.location || current.location,
    }));
  };

  const handleEditCancel = () => {
    setEditing(false);
    setSaveError(null);
    setForm(buildInitialForm(booking));
  };

  const handleSave = async () => {
    const priceCents = euroInputToCents(form.price);
    const durationMinutes = Number(form.durationMinutes);
    if (!form.startLocal || !Number.isFinite(durationMinutes) || durationMinutes <= 0 || priceCents === null) {
      setSaveError("Bitte Leistung, Beginn, Dauer und Preis prüfen.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const start = new Date(form.startLocal);
      const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
      await onBookingSave(booking, {
        serviceKey: form.serviceKey || selectedService?.serviceKey || booking.serviceKey,
        serviceLabel: form.serviceLabel || selectedService?.serviceLabel || translateServiceLabel(t, booking),
        durationMinutes,
        startISO: start.toISOString(),
        start: start.toISOString(),
        end: end.toISOString(),
        priceCents,
        location: form.location.trim(),
        customerNotes: form.customerNotes,
      });
      setEditing(false);
    } catch (err) {
      setSaveError(err?.response?.data?.message || t("bookings.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="team-delete-dialog-root">
      <div
        className="team-delete-dialog-overlay"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="team-delete-dialog team-booking-detail-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-booking-detail-title"
      >
        <div className="team-detail-header">
          <h2 id="team-booking-detail-title">{t("calendar.bookingDetail.title")}</h2>
          {canEditBooking && (
            <button
              type="button"
              className="team-booking-detail-edit"
              onClick={() => (editing ? handleEditCancel() : setEditing(true))}
              disabled={saving}
            >
              {editing ? "Abbrechen" : "Bearbeiten"}
            </button>
          )}
          <button
            type="button"
            className="team-close"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>

        {saveError && <div className="auth-alert is-error">{saveError}</div>}

        <dl className="team-detail-dl team-booking-detail-dl">
          <DetailRow
            label={t("bookings.col.service")}
            value={
              editing ? (
                <select
                  className="team-booking-detail-input"
                  value={form.serviceValue}
                  onChange={handleServiceChange}
                  disabled={saving}
                >
                  {currentServiceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <>
                {translateServiceLabel(t, booking)}
                {booking.isFreeFirstMassagesessel && (
                  <span className="badge" style={{ marginLeft: "0.35rem" }}>
                    {t("bookings.badge.freeTawan")}
                  </span>
                )}
                </>
              )
            }
          />
          <DetailRow
            label={t("calendar.bookingDetail.start")}
            value={
              editing ? (
                <input
                  className="team-booking-detail-input"
                  type="datetime-local"
                  value={form.startLocal}
                  onChange={updateForm("startLocal")}
                  disabled={saving}
                />
              ) : (
                formatLong(booking.start, dateLocale)
              )
            }
          />
          <DetailRow
            label={t("calendar.bookingDetail.end")}
            value={formatTime(booking.end, dateLocale)}
          />
          <DetailRow
            label={t("calendar.bookingDetail.duration")}
            value={
              editing ? (
                <input
                  className="team-booking-detail-input"
                  type="number"
                  min="1"
                  step="1"
                  value={form.durationMinutes}
                  onChange={updateForm("durationMinutes")}
                  disabled={saving}
                />
              ) : (
                `${booking.durationMinutes} ${t("common.minutes")}`
              )
            }
          />
          <DetailRow
            label={t("bookings.col.customer")}
            value={customerName || empty}
          />
          <DetailRow
            label={t("customers.field.email")}
            value={
              booking.customer?.email ? (
                <a href={`mailto:${booking.customer.email}`}>{booking.customer.email}</a>
              ) : (
                empty
              )
            }
          />
          <DetailRow
            label={t("customers.field.phone")}
            value={
              booking.customer?.phone ? (
                <a href={`tel:${booking.customer.phone}`}>{booking.customer.phone}</a>
              ) : (
                empty
              )
            }
          />
          <DetailRow
            label={t("bookings.col.price")}
            value={
              editing ? (
                <input
                  className="team-booking-detail-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={updateForm("price")}
                  disabled={saving}
                />
              ) : booking.priceCents === 0
                  ? t("common.free")
                  : formatEuro(booking.priceCents, locale)
            }
          />
          <DetailRow label={t("bookings.col.payment")} value={paymentLabel(booking)} />
          <DetailRow
            label={t("bookings.col.status")}
            value={
              canEditStatus ? (
                <select
                  value={booking.status}
                  disabled={statusSaving}
                  onChange={(e) => onStatusChange(booking, e.target.value)}
                  className="team-inline-select team-booking-detail-status"
                >
                  {statusOptions.map((val) => (
                    <option key={val} value={val}>
                      {statusLabel(t, val)}
                    </option>
                  ))}
                </select>
              ) : booking.status === "cancelled"
                ? [
                    statusLabel(t, booking.status),
                    cancelledByLabel(
                      t,
                      booking.cancelledBy,
                      booking.staffCancellationReason
                    ),
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : statusLabel(t, booking.status)
            }
          />
          {booking.status === "pending-email-verification" ? (
            <DetailRow
              label={t("calendar.bookingDetail.hint")}
              value={t("calendar.bookingDetail.pendingEmailVerification")}
              multiline
            />
          ) : null}
          {booking.status === "cancelled" && booking.cancelledAt ? (
            <DetailRow
              label={t("calendar.bookingDetail.cancelledAt")}
              value={formatLong(booking.cancelledAt, dateLocale)}
            />
          ) : null}
          {(booking.location || editing) ? (
            <DetailRow
              label={t("calendar.bookingDetail.location")}
              value={
                editing ? (
                  <input
                    className="team-booking-detail-input"
                    type="text"
                    value={form.location}
                    onChange={updateForm("location")}
                    disabled={saving}
                  />
                ) : (
                  booking.location
                )
              }
            />
          ) : null}
          {showBrand && booking.brand ? (
            <DetailRow label={t("calendar.bookingDetail.brand")} value={brandLabel(t, booking.brand)} />
          ) : null}
          {showSource && booking.source ? (
            <DetailRow
              label={t("customers.field.source")}
              value={sourceLabel(t, booking.source)}
            />
          ) : null}
          {booking.studioSchedule ? (
            <DetailRow
              label={t("calendar.bookingDetail.studioSchedule")}
              value={studioScheduleLabel(t, booking.studioSchedule)}
            />
          ) : null}
          {booking.hoffmannBookingOption ? (
            <DetailRow
              label={t("calendar.bookingDetail.option")}
              value={booking.hoffmannBookingOption}
            />
          ) : null}
          {booking.serviceAddress ? (
            <DetailRow
              label={t("calendar.bookingDetail.serviceAddress")}
              value={formatServiceAddress(booking.serviceAddress) || empty}
              multiline
            />
          ) : null}
          {booking.serviceAddress?.notes?.trim() ? (
            <DetailRow
              label={t("calendar.bookingDetail.addressNotes")}
              value={booking.serviceAddress.notes}
              multiline
            />
          ) : null}
          <DetailRow
            label={t("calendar.bookingDetail.customerNotes")}
            value={
              editing ? (
                <textarea
                  className="team-booking-detail-input team-booking-detail-textarea"
                  rows={4}
                  value={form.customerNotes}
                  onChange={updateForm("customerNotes")}
                  disabled={saving}
                />
              ) : (
                formatBookingCustomerNotes(booking) || empty
              )
            }
            multiline
          />
          <DetailRow
            label={t("bookings.col.notes")}
            value={internalNotesValue(booking)?.trim() ? internalNotesValue(booking) : empty}
            multiline
          />
          {booking.staffCancellationReason ? (
            <DetailRow
              label={t("calendar.bookingDetail.cancellationReason")}
              value={booking.staffCancellationReason}
            />
          ) : null}
          <DetailRow
            label={t("calendar.bookingDetail.created")}
            value={formatShortDate(booking.createdAt, dateLocale)}
          />
        </dl>

        <div className="team-delete-dialog-actions">
          {editing && (
            <button
              type="button"
              className="team-dialog-confirm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Wird gespeichert…" : "Speichern"}
            </button>
          )}
          <button type="button" className="team-dialog-cancel" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TeamBookingDetailDialog;
